import React from 'react';
import { useTranslation } from 'react-i18next';
import UserSelectionBase from './UserSelectionBase';

/**
 * Manager Selection Component - Handles manager selection with different rules per level
 * @param {Object} node - Current node: { id, level, name, manager, ... }
 * @param {Array|Object} selectedManager - Selected manager(s): [] for Level 1, {} for Level 2+
 * @param {Array} filteredManagers - Filtered managers for AutoComplete: [{ id, username }, ...]
 * @param {Function} searchManagers - Search function for filtering: (event) => void
 * @param {Function} onManagersChange - Change handler: (event) => void
 * @param {Object} errors - Validation errors: { manager: 'error message' }
 * @param {boolean} disabled - Whether to disable the field
 * @param {boolean} disableInviteUsers - Whether to disable adding users emails invitations for external users without privileges
 * @returns {JSX.Element} - AutoComplete component with level-specific configuration
 *
 * Logic:
 * - Level 1: Multi-select managers (optional, no validation)
 * - Level 2+: Single-select manager (required, with validation)
 * - Different placeholders and labels based on level
 * - Validation errors displayed for required fields
 */
export default function ManagerSelection({
  node,
  selectedManager,
  filteredManagers,
  searchManagers,
  onManagersChange,
  errors,
  disableInviteUsers = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  const isMultiple = node.level === 1;

  return (
    <UserSelectionBase
      label={isMultiple ? t('department_managers') : t('department_manager')}
      required={!isMultiple}
      multiple={isMultiple}
      value={selectedManager}
      suggestions={filteredManagers}
      completeMethod={searchManagers}
      onChange={onManagersChange}
      placeholder={
        isMultiple
          ? disableInviteUsers
            ? t('search_users')
            : t('search_users_or_type_emails')
          : disableInviteUsers
            ? t('search_user')
            : t('search_user_or_type_email')
      }
      errors={errors?.manager}
      enableKeyboardAdd={isMultiple && !disableInviteUsers}
      disabled={disabled}
    />
  );
}
